import { useCallback, useRef } from 'react';

const SCROLLABLE_OVERFLOW = /auto|scroll|overlay/;

// At least one whole pixel left to travel; fractional zoom leaves sub-pixel
// remainders at the edges that the browser will not actually scroll.
const hasRoom = (distance: number) => distance >= 1;

const canScrollElement = (el: Element, dx: number, dy: number): boolean => {
  const style = window.getComputedStyle(el);

  if (dy !== 0 && SCROLLABLE_OVERFLOW.test(style.overflowY)) {
    const max = el.scrollHeight - el.clientHeight;
    if (max > 0 && hasRoom(dy < 0 ? el.scrollTop : max - el.scrollTop)) return true;
  }

  if (dx !== 0 && SCROLLABLE_OVERFLOW.test(style.overflowX)) {
    const max = el.scrollWidth - el.clientWidth;
    // RTL scrollers count scrollLeft from 0 at the right edge down to -max.
    const [min, end] = style.direction === 'rtl' ? [-max, 0] : [0, max];
    if (max > 0 && hasRoom(dx < 0 ? el.scrollLeft - min : end - el.scrollLeft)) return true;
  }

  return false;
};

/**
 * Whether anything from `target` up to and including `root` can still scroll
 * by (dx, dy) — i.e. whether the browser has somewhere inside `root` to put
 * this wheel before chaining it to the page.
 */
export const wheelCanScrollWithin = (
  target: EventTarget | null,
  root: Element,
  dx: number,
  dy: number,
): boolean => {
  for (let el = target instanceof Element ? target : null; el; el = el.parentElement) {
    if (canScrollElement(el, dx, dy)) return true;
    if (el === root) break;
  }
  return false;
};

/**
 * Callback ref that keeps wheel/trackpad scrolling inside an unmasked overlay
 * from leaking to the page behind it.
 *
 * The desktop bot Drawer has no mask — the page stays usable beside it — so a
 * wheel the chat can't use chains to the document and scrolls the background:
 * the transcript already at its top/bottom edge, a short or empty transcript,
 * or the pointer over the header, tabs or composer. `overscroll-behavior` only
 * covers the first case, and only on elements that actually overflow.
 *
 * The wheel is swallowed only when nothing between the pointer and the root can
 * scroll further in that direction, so the transcript, card rows, option lists,
 * composer and dropdowns all keep scrolling natively. The listener is native
 * and non-passive: React's onWheel is passive and can't preventDefault.
 */
export default function useContainWheelScroll() {
  const detach = useRef<(() => void) | null>(null);

  return useCallback((node: HTMLElement | null) => {
    detach.current?.();
    detach.current = null;
    if (!node) return;

    const onWheel = (e: WheelEvent) => {
      // Pinch-zoom arrives as ctrl+wheel — leave browser zoom alone.
      if (e.defaultPrevented || e.ctrlKey) return;
      let dx = e.deltaX;
      let dy = e.deltaY;
      // A plain mouse wheel + Shift scrolls sideways (BotCardsRow relies on it)
      // but some browsers still report the delta on Y.
      if (e.shiftKey && dx === 0) {
        dx = dy;
        dy = 0;
      }
      if (dx === 0 && dy === 0) return;
      if (!wheelCanScrollWithin(e.target, node, dx, dy)) e.preventDefault();
    };

    node.addEventListener('wheel', onWheel, { passive: false });
    detach.current = () => node.removeEventListener('wheel', onWheel);
  }, []);
}
